import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { useAuthStore } from '@/stores/authStore';

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = useAuthStore.getState().accessToken;
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor to handle token refresh
apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      const refreshToken = useAuthStore.getState().refreshToken;

      if (refreshToken) {
        try {
          const response = await axios.post(`${API_BASE_URL}/auth/refresh`, {
            refreshToken,
          });

          const { accessToken, refreshToken: newRefreshToken } = response.data.data.tokens;
          useAuthStore.getState().setTokens(accessToken, newRefreshToken);

          if (originalRequest.headers) {
            originalRequest.headers.Authorization = `Bearer ${accessToken}`;
          }

          return apiClient(originalRequest);
        } catch {
          useAuthStore.getState().logout();
          window.location.href = '/login';
        }
      } else {
        useAuthStore.getState().logout();
        window.location.href = '/login';
      }
    }

    return Promise.reject(error);
  }
);

// Auth API
export const authApi = {
  login: async (email: string, password: string) => {
    const response = await apiClient.post('/auth/login', { email, password });
    return response.data;
  },
  logout: async () => {
    const refreshToken = useAuthStore.getState().refreshToken;
    if (refreshToken) {
      await apiClient.post('/auth/logout', { refreshToken });
    }
  },
  me: async () => {
    const response = await apiClient.get('/auth/me');
    return response.data;
  },
};

// Admin API
export const adminApi = {
  // Dashboard
  getDashboardStats: async () => {
    const response = await apiClient.get('/admin/dashboard/stats');
    return response.data;
  },

  // Products
  getProducts: async (params?: Record<string, unknown>) => {
    const response = await apiClient.get('/admin/products', { params });
    return response.data;
  },
  getProduct: async (id: string) => {
    const response = await apiClient.get(`/admin/products/${id}`);
    return response.data;
  },
  createProduct: async (data: Record<string, unknown>) => {
    const response = await apiClient.post('/admin/products', data);
    return response.data;
  },
  updateProduct: async (id: string, data: Record<string, unknown>) => {
    const response = await apiClient.put(`/admin/products/${id}`, data);
    return response.data;
  },
  deleteProduct: async (id: string) => {
    const response = await apiClient.delete(`/admin/products/${id}`);
    return response.data;
  },
  deleteAllProducts: async () => {
    const response = await apiClient.delete('/admin/products?confirm=true');
    return response.data;
  },
  deleteSelectedProducts: async (ids: string[]) => {
    const response = await apiClient.post('/admin/products/delete-batch', { ids });
    return response.data;
  },

  // Categories
  getCategories: async () => {
    const response = await apiClient.get('/admin/categories');
    return response.data;
  },
  createCategory: async (data: Record<string, unknown>) => {
    const response = await apiClient.post('/admin/categories', data);
    return response.data;
  },
  updateCategory: async (id: string, data: Record<string, unknown>) => {
    const response = await apiClient.put(`/admin/categories/${id}`, data);
    return response.data;
  },
  deleteCategory: async (id: string) => {
    const response = await apiClient.delete(`/admin/categories/${id}`);
    return response.data;
  },

  // Users
  getUsers: async (params?: Record<string, unknown>) => {
    const response = await apiClient.get('/admin/users', { params });
    return response.data;
  },
  getUser: async (id: string) => {
    const response = await apiClient.get(`/admin/users/${id}`);
    return response.data;
  },
  updateUser: async (id: string, data: Record<string, unknown>) => {
    const response = await apiClient.put(`/admin/users/${id}`, data);
    return response.data;
  },
  deleteUser: async (id: string) => {
    const response = await apiClient.delete(`/admin/users/${id}`);
    return response.data;
  },

  // Reviews
  getReviews: async (params?: Record<string, unknown>) => {
    const response = await apiClient.get('/admin/reviews', { params });
    return response.data;
  },
  updateReview: async (id: string, data: Record<string, unknown>) => {
    const response = await apiClient.put(`/admin/reviews/${id}`, data);
    return response.data;
  },
  deleteReview: async (id: string) => {
    const response = await apiClient.delete(`/admin/reviews/${id}`);
    return response.data;
  },

  // Banners
  getBanners: async () => {
    const response = await apiClient.get('/admin/banners');
    return response.data;
  },
  createBanner: async (data: Record<string, unknown>) => {
    const response = await apiClient.post('/admin/banners', data);
    return response.data;
  },
  updateBanner: async (id: string, data: Record<string, unknown>) => {
    const response = await apiClient.put(`/admin/banners/${id}`, data);
    return response.data;
  },
  deleteBanner: async (id: string) => {
    const response = await apiClient.delete(`/admin/banners/${id}`);
    return response.data;
  },

  // Suppliers
  getSuppliers: async () => {
    const response = await apiClient.get('/admin/suppliers');
    return response.data;
  },
  createSupplier: async (data: Record<string, unknown>) => {
    const response = await apiClient.post('/admin/suppliers', data);
    return response.data;
  },
  updateSupplier: async (id: string, data: Record<string, unknown>) => {
    const response = await apiClient.put(`/admin/suppliers/${id}`, data);
    return response.data;
  },
  deleteSupplier: async (id: string) => {
    const response = await apiClient.delete(`/admin/suppliers/${id}`);
    return response.data;
  },

  // Settings
  getSettings: async () => {
    const response = await apiClient.get('/admin/settings');
    return response.data;
  },
  updateSetting: async (key: string, value: unknown) => {
    const response = await apiClient.put(`/admin/settings/${key}`, { value });
    return response.data;
  },
};
